'use client';

import 'react-image-crop/dist/ReactCrop.css';

import { Slot } from '@radix-ui/react-slot';
import { CropIcon, RotateCcwIcon, ZoomIn, ZoomOut } from 'lucide-react';
import * as React from 'react';
import {
  type ComponentProps,
  createContext,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type PercentCrop,
  type PixelCrop,
  type ReactCropProps,
} from 'react-image-crop';

import { cn } from '../../../utils/cn';
import { Button } from '../../button';
import { Slider } from '../../slider';
import {
  centerPan,
  clampPan,
  coverSize,
  MAX_CROP_ZOOM,
  MIN_CROP_ZOOM,
  type CropPan,
  type NaturalCropRect,
  naturalCropRect,
  zoomAround,
} from './crop-zoom';

const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number | undefined,
): PercentCrop =>
  centerCrop(
    aspect
      ? makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          aspect,
          mediaWidth,
          mediaHeight,
        )
      : { x: 0, y: 0, width: 90, height: 90, unit: '%' },
    mediaWidth,
    mediaHeight,
  );

/** Byte length of a data URL without fetch (CSP connect-src may block data:). */
const dataUrlByteLength = (dataUrl: string): number => {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) {
    return 0;
  }
  const base64 = dataUrl.slice(comma + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
};

type CropOutputOptions = {
  /** Canvas export MIME. Default `image/png`. Prefer `image/jpeg` for wide banners. */
  mimeType?: string;
  /** 0–1 for lossy formats (`image/jpeg`, `image/webp`). */
  quality?: number;
  /**
   * Cap the longer output edge (natural crop size × scaleFactor).
   * Keeps retina headers sharp without multi‑MB uploads.
   */
  maxOutputEdge?: number;
};

const getCroppedImage = async (
  imageSrc: HTMLImageElement,
  scaleFactor: number,
  pixelCrop: PixelCrop | null,
  maxImageSize: number,
  options: CropOutputOptions = {},
  naturalRect?: NaturalCropRect,
): Promise<string> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Context is null, this should never happen.');
  }

  // pixelCrop is in *displayed* CSS pixels; map to the image's natural pixels
  // so export resolution matches the source photo — not the small crop UI.
  // Zoom mode passes `naturalRect` directly (pan + zoom), so layout width is ignored.
  const scaleX =
    naturalRect || !pixelCrop ? 1 : imageSrc.naturalWidth / imageSrc.width;
  const scaleY =
    naturalRect || !pixelCrop ? 1 : imageSrc.naturalHeight / imageSrc.height;
  const sourceX = naturalRect?.x ?? (pixelCrop?.x ?? 0) * scaleX;
  const sourceY = naturalRect?.y ?? (pixelCrop?.y ?? 0) * scaleY;
  const naturalCropWidth =
    naturalRect?.width ?? (pixelCrop?.width ?? 1) * scaleX;
  const naturalCropHeight =
    naturalRect?.height ?? (pixelCrop?.height ?? 1) * scaleY;

  let outputWidth = Math.max(1, Math.floor(naturalCropWidth * scaleFactor));
  let outputHeight = Math.max(1, Math.floor(naturalCropHeight * scaleFactor));

  const maxEdge = options.maxOutputEdge;
  if (maxEdge != null && maxEdge > 0) {
    const longest = Math.max(outputWidth, outputHeight);
    if (longest > maxEdge) {
      const edgeScale = maxEdge / longest;
      outputWidth = Math.max(1, Math.floor(outputWidth * edgeScale));
      outputHeight = Math.max(1, Math.floor(outputHeight * edgeScale));
    }
  }

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const downscaling =
    outputWidth < naturalCropWidth || outputHeight < naturalCropHeight;
  ctx.imageSmoothingEnabled = downscaling;
  if (downscaling) {
    ctx.imageSmoothingQuality = 'high';
  }

  ctx.drawImage(
    imageSrc,
    sourceX,
    sourceY,
    naturalCropWidth,
    naturalCropHeight,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  const mimeType = options.mimeType ?? 'image/png';
  const croppedImageUrl =
    mimeType === 'image/png'
      ? canvas.toDataURL(mimeType)
      : canvas.toDataURL(mimeType, options.quality ?? 0.92);

  if (dataUrlByteLength(croppedImageUrl) > maxImageSize) {
    if (scaleFactor <= 0.05) {
      return croppedImageUrl;
    }
    return await getCroppedImage(
      imageSrc,
      scaleFactor * 0.9,
      pixelCrop,
      maxImageSize,
      options,
      naturalRect,
    );
  }

  return croppedImageUrl;
};

type FrameSize = { w: number; h: number };

type ImageCropContextType = {
  file: File;
  maxImageSize: number;
  imgSrc: string;
  crop: PercentCrop | undefined;
  completedCrop: PixelCrop | null;
  imgRef: RefObject<HTMLImageElement | null>;
  onCrop?: (croppedImage: string) => void;
  reactCropProps: Omit<ReactCropProps, 'onChange' | 'onComplete' | 'children'>;
  handleChange: (pixelCrop: PixelCrop, percentCrop: PercentCrop) => void;
  handleComplete: (
    pixelCrop: PixelCrop,
    percentCrop: PercentCrop,
  ) => Promise<void>;
  onImageLoad: (e: SyntheticEvent<HTMLImageElement>) => void;
  applyCrop: () => Promise<void>;
  resetCrop: () => void;
  zoom: number;
  minZoom: number;
  maxZoom: number;
  setZoom: (zoom: number) => void;
  nudgeZoom: (delta: number) => void;
  pan: CropPan;
  frame: FrameSize;
  natural: FrameSize;
  reportFrame: (w: number, h: number) => void;
  commitPan: (pan: CropPan) => void;
};

const ImageCropContext = createContext<ImageCropContextType | null>(null);

const useImageCrop = () => {
  const context = useContext(ImageCropContext);
  if (!context) {
    throw new Error('ImageCrop components must be used within ImageCrop');
  }
  return context;
};

export type ImageCropProps = {
  file: File;
  maxImageSize?: number;
  onCrop?: (croppedImage: string) => void;
  children: ReactNode;
  onChange?: ReactCropProps['onChange'];
  onComplete?: ReactCropProps['onComplete'];
  /** Export MIME (default PNG). Use JPEG for large banner crops. */
  outputMimeType?: string;
  /** Lossy quality 0–1 when `outputMimeType` is jpeg/webp. */
  outputQuality?: number;
  /** Cap longest output edge in natural pixels (e.g. 2400 for headers). */
  maxOutputEdge?: number;
} & Omit<ReactCropProps, 'onChange' | 'onComplete' | 'children'>;

export const ImageCrop = ({
  file,
  maxImageSize = 1024 * 1024 * 5,
  onCrop,
  children,
  onChange,
  onComplete,
  outputMimeType,
  outputQuality,
  maxOutputEdge,
  ...reactCropProps
}: ImageCropProps) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const frameRef = useRef<FrameSize>({ w: 0, h: 0 });
  const naturalRef = useRef<FrameSize>({ w: 0, h: 0 });
  const panRef = useRef<CropPan>({ x: 0, y: 0 });
  const zoomRef = useRef(MIN_CROP_ZOOM);
  const positionedRef = useRef(false);
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<PercentCrop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [initialCrop, setInitialCrop] = useState<PercentCrop>();
  const [zoom, setZoomState] = useState(MIN_CROP_ZOOM);
  const [pan, setPanState] = useState<CropPan>({ x: 0, y: 0 });
  const [frame, setFrameState] = useState<FrameSize>({ w: 0, h: 0 });
  const [natural, setNatural] = useState<FrameSize>({ w: 0, h: 0 });
  const zoomMode = typeof reactCropProps.aspect === 'number';

  const outputOptions = useMemo<CropOutputOptions>(
    () => ({
      mimeType: outputMimeType,
      quality: outputQuality,
      maxOutputEdge,
    }),
    [outputMimeType, outputQuality, maxOutputEdge],
  );

  const commitPan = useCallback((next: CropPan) => {
    const currentFrame = frameRef.current;
    const currentNatural = naturalRef.current;
    const display = coverSize(
      currentFrame.w,
      currentFrame.h,
      currentNatural.w,
      currentNatural.h,
      zoomRef.current,
    );
    const clamped = clampPan(
      next,
      currentFrame.w,
      currentFrame.h,
      display.width,
      display.height,
    );
    panRef.current = clamped;
    setPanState(clamped);
  }, []);

  const centerImage = useCallback((nextZoom = MIN_CROP_ZOOM) => {
    const currentFrame = frameRef.current;
    const currentNatural = naturalRef.current;
    const clampedZoom = Math.min(
      MAX_CROP_ZOOM,
      Math.max(MIN_CROP_ZOOM, nextZoom),
    );
    zoomRef.current = clampedZoom;
    setZoomState(clampedZoom);
    if (currentFrame.w <= 0 || currentNatural.w <= 0) return;
    const display = coverSize(
      currentFrame.w,
      currentFrame.h,
      currentNatural.w,
      currentNatural.h,
      clampedZoom,
    );
    const next = centerPan(
      currentFrame.w,
      currentFrame.h,
      display.width,
      display.height,
    );
    panRef.current = next;
    setPanState(next);
    positionedRef.current = true;
  }, []);

  const setZoom = useCallback((nextZoom: number) => {
    const prev = zoomRef.current;
    const clamped = Math.min(MAX_CROP_ZOOM, Math.max(MIN_CROP_ZOOM, nextZoom));
    const currentFrame = frameRef.current;
    const currentNatural = naturalRef.current;
    if (currentFrame.w > 0 && currentNatural.w > 0 && clamped !== prev) {
      const nextPan = zoomAround(
        panRef.current,
        prev,
        clamped,
        currentFrame.w,
        currentFrame.h,
        currentNatural.w,
        currentNatural.h,
        currentFrame.w / 2,
        currentFrame.h / 2,
      );
      panRef.current = nextPan;
      setPanState(nextPan);
    }
    zoomRef.current = clamped;
    setZoomState(clamped);
  }, []);

  const nudgeZoom = useCallback(
    (delta: number) => {
      setZoom(zoomRef.current + delta);
    },
    [setZoom],
  );

  const reportFrame = useCallback(
    (w: number, h: number) => {
      frameRef.current = { w, h };
      setFrameState({ w, h });
      if (!positionedRef.current) {
        centerImage(MIN_CROP_ZOOM);
        return;
      }
      commitPan(panRef.current);
    },
    [centerImage, commitPan],
  );

  useEffect(() => {
    positionedRef.current = false;
    naturalRef.current = { w: 0, h: 0 };
    zoomRef.current = MIN_CROP_ZOOM;
    panRef.current = { x: 0, y: 0 };
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setNatural({ w: 0, h: 0 });
      setZoomState(MIN_CROP_ZOOM);
      setPanState({ x: 0, y: 0 });
      setImgSrc(reader.result?.toString() || '');
    });
    reader.readAsDataURL(file);
  }, [file]);

  const onImageLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (zoomMode) {
        naturalRef.current = {
          w: img.naturalWidth,
          h: img.naturalHeight,
        };
        setNatural({ w: img.naturalWidth, h: img.naturalHeight });
        positionedRef.current = false;
        centerImage(MIN_CROP_ZOOM);
        return;
      }
      const { width, height } = img;
      const newCrop = centerAspectCrop(width, height, reactCropProps.aspect);
      setCrop(newCrop);
      setInitialCrop(newCrop);
    },
    [centerImage, reactCropProps.aspect, zoomMode],
  );

  const handleChange = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    setCrop(percentCrop);
    onChange?.(pixelCrop, percentCrop);
  };

  // biome-ignore lint/suspicious/useAwait: "onComplete is async"
  const handleComplete = async (
    pixelCrop: PixelCrop,
    percentCrop: PercentCrop,
  ) => {
    setCompletedCrop(pixelCrop);
    onComplete?.(pixelCrop, percentCrop);
  };

  const applyCrop = async () => {
    if (!imgRef.current) return;

    if (zoomMode) {
      const currentFrame = frameRef.current;
      const currentNatural = naturalRef.current;
      if (currentFrame.w <= 0 || currentNatural.w <= 0) return;
      const rect = naturalCropRect(
        panRef.current,
        zoomRef.current,
        currentFrame.w,
        currentFrame.h,
        currentNatural.w,
        currentNatural.h,
      );
      const croppedImage = await getCroppedImage(
        imgRef.current,
        1,
        null,
        maxImageSize,
        outputOptions,
        rect,
      );
      onCrop?.(croppedImage);
      return;
    }

    if (!completedCrop) return;

    const croppedImage = await getCroppedImage(
      imgRef.current,
      1,
      completedCrop,
      maxImageSize,
      outputOptions,
    );

    onCrop?.(croppedImage);
  };

  const resetCrop = () => {
    if (zoomMode) {
      positionedRef.current = false;
      centerImage(MIN_CROP_ZOOM);
      return;
    }
    if (initialCrop) {
      setCrop(initialCrop);
      setCompletedCrop(null);
    }
  };

  const contextValue: ImageCropContextType = {
    file,
    maxImageSize,
    imgSrc,
    crop,
    completedCrop,
    imgRef,
    onCrop,
    reactCropProps,
    handleChange,
    handleComplete,
    onImageLoad,
    applyCrop,
    resetCrop,
    zoom,
    minZoom: MIN_CROP_ZOOM,
    maxZoom: MAX_CROP_ZOOM,
    setZoom,
    nudgeZoom,
    pan,
    frame,
    natural,
    reportFrame,
    commitPan,
  };

  return (
    <ImageCropContext.Provider value={contextValue}>
      {children}
    </ImageCropContext.Provider>
  );
};

export type ImageCropContentProps = {
  style?: CSSProperties;
  className?: string;
};

export const ImageCropContent = ({
  style,
  className,
}: ImageCropContentProps) => {
  const {
    imgSrc,
    crop,
    handleChange,
    handleComplete,
    onImageLoad,
    imgRef,
    reactCropProps,
    zoom,
    pan,
    frame,
    natural,
    reportFrame,
    commitPan,
    setZoom,
    nudgeZoom,
  } = useImageCrop();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const panDrag = useRef<{
    x: number;
    y: number;
    pan: CropPan;
  } | null>(null);
  const pinchDrag = useRef<{ dist: number; zoom: number } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  const aspect = reactCropProps.aspect;
  const zoomMode = typeof aspect === 'number' && aspect > 0;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !zoomMode) return;
    const measure = () => {
      const maxW = host.clientWidth;
      const maxH = Math.min(400, window.innerHeight * 0.5);
      let w = maxW;
      let h = w / aspect;
      if (h > maxH) {
        h = maxH;
        w = h * aspect;
      }
      reportFrame(Math.max(1, Math.floor(w)), Math.max(1, Math.floor(h)));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [aspect, reportFrame, zoomMode]);

  if (zoomMode) {
    const display = coverSize(frame.w, frame.h, natural.w, natural.h, zoom);

    const endPointer = (pointerId: number) => {
      pointers.current.delete(pointerId);
      if (pointers.current.size < 2) pinchDrag.current = null;
      if (pointers.current.size !== 1) panDrag.current = null;
    };

    return (
      <div ref={hostRef} className={cn('w-full', className)} style={style}>
        <div
          className="relative mx-auto cursor-grab touch-none overflow-hidden rounded-md bg-background active:cursor-grabbing"
          style={{
            width: frame.w > 0 ? frame.w : '100%',
            height: frame.h > 0 ? frame.h : undefined,
          }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            pointers.current.set(event.pointerId, {
              x: event.clientX,
              y: event.clientY,
            });
            if (pointers.current.size === 1) {
              panDrag.current = {
                x: event.clientX,
                y: event.clientY,
                pan,
              };
            } else {
              panDrag.current = null;
            }
          }}
          onPointerMove={(event) => {
            if (!pointers.current.has(event.pointerId)) return;
            pointers.current.set(event.pointerId, {
              x: event.clientX,
              y: event.clientY,
            });
            const points = [...pointers.current.values()];
            if (points.length >= 2) {
              const [a, b] = points;
              if (!a || !b) return;
              const dist = Math.hypot(a.x - b.x, a.y - b.y);
              if (!pinchDrag.current || pinchDrag.current.dist === 0) {
                pinchDrag.current = { dist, zoom };
                return;
              }
              setZoom(pinchDrag.current.zoom * (dist / pinchDrag.current.dist));
              return;
            }
            const drag = panDrag.current;
            if (!drag) return;
            commitPan({
              x: drag.pan.x + (event.clientX - drag.x),
              y: drag.pan.y + (event.clientY - drag.y),
            });
          }}
          onPointerUp={(event) => endPointer(event.pointerId)}
          onPointerCancel={(event) => endPointer(event.pointerId)}
          onWheel={(event) => {
            nudgeZoom(event.deltaY > 0 ? -0.12 : 0.12);
          }}
        >
          {imgSrc ? (
            <img
              alt=""
              className="pointer-events-none absolute top-0 left-0 max-w-none select-none"
              draggable={false}
              onLoad={onImageLoad}
              ref={imgRef}
              src={imgSrc}
              style={{
                width: display.width > 0 ? display.width : undefined,
                height: display.height > 0 ? display.height : undefined,
                transform: `translate(${pan.x}px, ${pan.y}px)`,
              }}
            />
          ) : null}
        </div>
      </div>
    );
  }

  const shadcnStyle = {
    '--rc-border-color': 'var(--color-border)',
    '--rc-focus-color': 'var(--color-primary)',
  } as CSSProperties;

  return (
    <ReactCrop
      className={cn('max-h-[277px] max-w-full touch-none', className)}
      crop={crop}
      onChange={handleChange}
      onComplete={handleComplete}
      style={{ ...shadcnStyle, ...style }}
      {...reactCropProps}
    >
      {imgSrc ? (
        <img
          alt="crop"
          className="size-full select-none"
          draggable={false}
          onLoad={onImageLoad}
          ref={imgRef}
          src={imgSrc}
        />
      ) : null}
    </ReactCrop>
  );
};

export type ImageCropZoomProps = {
  label: string;
};

export const ImageCropZoom = ({ label }: ImageCropZoomProps) => {
  const { zoom, minZoom, maxZoom, setZoom, reactCropProps } = useImageCrop();
  if (typeof reactCropProps.aspect !== 'number') return null;

  return (
    <div className="flex items-center gap-3">
      <ZoomOut className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <Slider
        aria-label={label}
        className="flex-1"
        min={minZoom}
        max={maxZoom}
        step={0.01}
        value={[zoom]}
        onValueChange={(value) => {
          const next = value[0];
          if (next != null) setZoom(next);
        }}
      />
      <ZoomIn className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </div>
  );
};

export type ImageCropApplyProps = ComponentProps<'button'> & {
  asChild?: boolean;
};

export const ImageCropApply = ({
  asChild = false,
  children,
  onClick,
  ...props
}: ImageCropApplyProps) => {
  const { applyCrop } = useImageCrop();

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    await applyCrop();
    onClick?.(e);
  };

  if (asChild) {
    return (
      <Slot
        onClick={handleClick}
        {...(props as React.ComponentPropsWithoutRef<'button'>)}
      >
        {children}
      </Slot>
    );
  }

  return (
    <Button
      onClick={handleClick}
      size="icon"
      variant="ghost"
      {...(props as React.ComponentPropsWithoutRef<typeof Button>)}
    >
      {children ?? <CropIcon className="size-4" />}
    </Button>
  );
};

export type ImageCropResetProps = ComponentProps<'button'> & {
  asChild?: boolean;
};

export const ImageCropReset = ({
  asChild = false,
  children,
  onClick,
  ...props
}: ImageCropResetProps) => {
  const { resetCrop } = useImageCrop();

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    resetCrop();
    onClick?.(e);
  };

  if (asChild) {
    return (
      <Slot
        onClick={handleClick}
        {...(props as React.ComponentPropsWithoutRef<'button'>)}
      >
        {children}
      </Slot>
    );
  }

  return (
    <Button
      onClick={handleClick}
      size="icon"
      variant="ghost"
      {...(props as React.ComponentPropsWithoutRef<typeof Button>)}
    >
      {children ?? <RotateCcwIcon className="size-4" />}
    </Button>
  );
};

// Keep the original Cropper component for backward compatibility
export type CropperProps = Omit<ReactCropProps, 'onChange'> & {
  file: File;
  maxImageSize?: number;
  onCrop?: (croppedImage: string) => void;
  onChange?: ReactCropProps['onChange'];
  outputMimeType?: string;
  outputQuality?: number;
  maxOutputEdge?: number;
};

export const Cropper = ({
  onChange,
  onComplete,
  onCrop,
  style,
  className,
  file,
  maxImageSize,
  outputMimeType,
  outputQuality,
  maxOutputEdge,
  ...props
}: CropperProps) => (
  <ImageCrop
    file={file}
    maxImageSize={maxImageSize}
    onChange={onChange}
    onComplete={onComplete}
    onCrop={onCrop}
    outputMimeType={outputMimeType}
    outputQuality={outputQuality}
    maxOutputEdge={maxOutputEdge}
    {...(props as Omit<ReactCropProps, 'onChange' | 'onComplete' | 'onCrop'>)}
  >
    <ImageCropContent className={className} style={style} />
  </ImageCrop>
);
